/* @flow */
import Raven from "raven-js";
import React, { useContext } from "react";
import prefixStyles from "nx-components/utils/prefixStyles";
import ScreenReaderOnlyLiveRegion from "nx-components/ScreenReaderOnlyLiveRegion";
import { withColorsProvider } from "nx-components/ColorsProvider";
import type { ColorSet } from "nx-components/ColorsProvider";
import NoStyleButton from "nx-components/NoStyleButton";
import LanguageContext from "nx-components/ContextWrappers/LanguageContext";

// The server has a tremendous volume of error messages to provide, and not all
// of them have proper sentence punctuation. Report bad messages to Sentry.
// For now we will also check that the toast is capitalized, but it may not be
// possible in the future (a toast which starts with a device name, for example)
const INVALID_FIRST_CHARS = /^[a-z]/;
const VALID_LAST_CHARS = /[.!?"']$/;
function toSentence(text) {
  const badFirstChar = text.match(INVALID_FIRST_CHARS);
  const badPunctuation = !text.match(VALID_LAST_CHARS);

  if (!badFirstChar && !badPunctuation) {
    return text;
  }

  let newText = text;
  if (badFirstChar) {
    newText = newText[0].toUpperCase() + newText.slice(1);
  }
  if (badPunctuation) {
    newText += ".";
  }
  Raven.captureMessage("A toast was shown to the user with bad punctuation.", {
    level: "warning",
    extra: {
      providedText: text,
      displayedText: newText,
    },
  });

  return newText;
}

type Props = {
  id: number,
  colors: ColorSet,
  hostRef: (?HTMLElement) => mixed,
  persistent: boolean,
  text: string,
  onDismiss: () => void,
};

const ToastMsg = (props: Props) => {
  const languageContext = useContext(LanguageContext);
  const language = languageContext.localeSet.nexia_mobile.utils.ToastMsg;
  const { id, colors, hostRef, persistent, text, onDismiss } = props;
  const styles = prefixStyles({
    wrapper: {
      // This must match the style of the PosedToastMsg pose that the PosedToastMsg starts in
      opacity: 0,
      height: 0,
      overflow: "hidden",
      transform: "translateX(-100vw)",
    },
    toast: {
      display: "flex",
      alignItems: "center",

      background: "#323232",
      borderRadius: "2px",
      boxShadow: "0 2px 5px 0 rgba(0, 0, 0, 0.26)",
      fontSize: "0.8em",
      margin: "0.5em auto 0",
      maxWidth: "30em",
      overflow: "hidden",
      padding: "0.75em",
      pointerEvents: "auto",
    },
    message: {
      color: "#f1f1f1",
    },
    button: {
      color: colors.PRIMARY_GRADIENT_LIGHT,
      flex: "0 0 auto",
      fontSize: "1.2em",
      paddingLeft: "0.75em",
    },
  });

  if (!persistent) {
    setTimeout(onDismiss, 3200);
  }

  const sentence = toSentence(text);

  return (
    <div data-component-name="ToastMsg" style={styles.wrapper} ref={hostRef}>
      <div style={styles.toast}>
        <div style={styles.message}>{sentence}</div>
        <ScreenReaderOnlyLiveRegion id={`toast-${id}`}>
          {sentence}
        </ScreenReaderOnlyLiveRegion>
        {persistent ? (
          <NoStyleButton style={styles.button} onTap={onDismiss}>
            {language.dismiss}
          </NoStyleButton>
        ) : null}
      </div>
    </div>
  );
};

export default withColorsProvider(ToastMsg);



// WEBPACK FOOTER //
// ./src/components/Toaster/ToastMsg.jsx